import { useContext, useEffect, useMemo, useRef, useState, startTransition, useTransition } from 'react';
import useMediaQuery from '@mui/material/useMediaQuery';
import type { Theme } from '@mui/material/styles';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Container,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  LinearProgress,
  Stack,
  Typography,
  Divider,
  Avatar,
  Tooltip,
  ListSubheader,
  Menu,
  MenuItem,
  Chip,
  Badge as MuiBadge,
} from '@mui/material';
import { api } from '../../lib/api';
import { preloadRoute } from '../../lib/routePreload';
import { MODERATION_COUNTS_KEY } from '../../lib/api/modules/healthQa';
import {
  Menu as MenuIcon,
  Home, Users, DollarSign, BarChart3, Settings, Moon, Sun,
  Stethoscope, UserCircle, Calendar, UserPlus, Boxes,
  UsersRound, ShieldCheck, Scale, Flag, LogOut, TrendingUp, HelpCircle, Keyboard,
  Bookmark, Store, Mail, CalendarCheck, Bed, Microscope, AlertTriangle,
  FileText, ClipboardList, Shield, Bot, Building2, ArrowRightLeft,
  Bell, Send, Package, Share2, ClipboardCheck, X, Star, Activity, FlaskConical, ShieldOff, Images,
  ChevronsUpDown, BookOpen, CreditCard, Globe, Truck, ShoppingBag,
  Newspaper, Smartphone, CalendarClock,
  Wallet, Pill, ScanLine, Receipt as ReceiptIcon, UserRoundPlus, Calculator, FileSignature, LineChart, Tags, Receipt, ReceiptText, PackagePlus, ListChecks, BookLock, Eye, Smile, Glasses,
} from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { navLabel, navSectionLabel } from '../../i18n/navLabel';
import { enumLabel } from '../../i18n/enumLabel';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissions } from '../../contexts/PermissionContext';
import { useAiModule } from '../../hooks/useAiModule';
import { useActivePharmacy } from '../../hooks/useActivePharmacy';
import { AiUpsellPanel } from '../ai/AiUpsellProvider';
import { getRouteRequirement, routeUpsellModule } from '../../lib/routePermissions';
import { useTenant } from '../../contexts/TenantContext';
import { useWebSocketContext } from '../../contexts/WebSocketContext';
import { ColorModeContext } from '../../App';
import { MinimizedPatientTabs } from '../patients/MinimizedPatientTabs';
import { GlobalPatientWindows } from '../patients/GlobalPatientWindows';
import { HelpModal } from '../help/HelpModal';
import { KeyboardShortcutsModal } from '../common/KeyboardShortcutsModal';
import NotificationBell from '../common/NotificationBell';
import { BranchSwitcher } from './BranchSwitcher';
import { QuickPatientSearch } from './QuickPatientSearch';
import { design } from '../../theme/clinveaDesign';
import { AppTopBar } from './AppTopBar';
import { AiStaffChatPanel, AiChatFab } from '../ai/AiStaffChatPanel';
import { LearnMoreButton } from '../common/LearnMoreButton';
import { avatarSrc } from '../../lib/mediaUrls';
import { PharmacyImportTracker } from '../pharmacy/importTracker';
import { PendingDeletionDialog } from '../account/PendingDeletionDialog';
import { LAB_FEATURES, useLabFeatures } from '../../hooks/useLabFeatures';

interface LayoutProps {
  children: React.ReactNode;
}

interface NavSection {
  title?: string;
  items: {
    label: string;
    path: string;
    icon: typeof Home;
    tourId?: string;
  }[];
}

const SIDEBAR_WIDTH = 268;
const SIDEBAR_WIDTH_COLLAPSED = 76;
// Rail width below lg. Narrower than the desktop collapsed rail because phone width is scarce.
const SIDEBAR_WIDTH_RAIL = 60;
const SIDEBAR_COLLAPSED_KEY = 'staff_sidebar_collapsed';

// Scrollable but no visible scrollbar track — cross-browser (Firefox, WebKit/Blink, legacy Edge).
const hiddenScrollbarSx = {
  scrollbarWidth: 'none' as const,
  msOverflowStyle: 'none' as const,
  '&::-webkit-scrollbar': { display: 'none' },
};

// Master nav — all possible non-super-admin routes.
// The server controls which paths are visible via visiblePaths.
// section=null → top-level item; named section → grouped under a heading in the sidebar
const MASTER_NAV: Array<{ path: string; label: string; icon: typeof Home; section: string | null }> = [
  { path: '/dashboard',              label: 'Dashboard',    icon: Home,          section: null },
  { path: '/notifications',          label: 'Mail',         icon: Mail,          section: null },
  { path: '/lab/setup',              label: 'Lab Setup',    icon: ClipboardCheck, section: null },
  { path: '/tokens',                 label: 'Appointments', icon: Calendar,      section: 'Patient Care' },
  { path: '/patients/register',      label: 'Register',     icon: UserPlus,      section: 'Patient Care' },
  { path: '/patients',               label: 'Patients',     icon: Users,         section: 'Patient Care' },
  { path: '/referrals',              label: 'Specialist Referrals', icon: Share2, section: 'Patient Care' },
  { path: '/transfers',              label: 'Patient Transfers', icon: ArrowRightLeft, section: 'Patient Care' },
  { path: '/inpatients',             label: 'Inpatients',   icon: Bed,           section: 'Patient Care' },
  // Active nav rows that had no sidebar entry, so no menu ever showed them (medopd-docs 107).
  { path: '/procedures',             label: 'Procedure Room', icon: Activity,     section: 'Patient Care' },
  { path: '/treatment-plans',        label: 'Treatment Plans', icon: ClipboardCheck, section: 'Patient Care' },
  { path: '/work-orders',            label: 'Work Orders', icon: ClipboardList, section: 'Patient Care' },
  { path: '/eye-care',               label: 'Eye Care', icon: Eye, section: 'Patient Care' },
  { path: '/optical',                label: 'Optical Shop', icon: Glasses, section: 'Patient Care' },
  { path: '/dental',                 label: 'Dental Care', icon: Smile, section: 'Patient Care' },
  { path: '/medications',            label: 'Medications',  icon: Store,         section: 'Patient Care' },
  { path: '/billing',                label: 'Billing',      icon: DollarSign,    section: 'Patient Care' },
  { path: '/billing/queue',          label: 'Bill Queue',   icon: DollarSign,    section: 'Patient Care' },
  { path: '/billing/cash-close',     label: 'Day Close',    icon: Wallet,        section: 'Patient Care' },
  { path: '/follow-ups',             label: 'Follow-ups',   icon: Calendar,      section: 'Patient Care' },
  { path: '/doctor/schedule-config', label: 'Schedule',     icon: Settings,      section: 'Patient Care' },
  { path: '/doctor/appointments',    label: 'Recurring',    icon: CalendarCheck, section: 'Patient Care' },
  { path: '/doctor/public-profile',   label: 'Public Profile', icon: Globe,       section: 'Public Presence' },
  { path: '/doctor/bookings',        label: 'Online Bookings', icon: CalendarCheck, section: 'Public Presence' },
  { path: '/doctor/practices',       label: 'My Practices', icon: Building2,     section: 'Public Presence' },
  { path: '/doctor/articles',        label: 'Articles',     icon: Newspaper,     section: 'Public Presence' },
  { path: '/doctor/questions',       label: 'Patient Questions', icon: HelpCircle, section: 'Public Presence' },
  { path: '/doctor/notes',           label: 'Note History', icon: FileText,      section: 'Clinical Work' },
  { path: '/doctor/prescriptions',   label: 'Rx History',   icon: ClipboardList, section: 'Clinical Work' },
  { path: '/doctor/templates',       label: 'Templates',    icon: Bookmark,      section: 'Clinical Work' },
  { path: '/doctor/lab-review',      label: 'Lab AI Review', icon: FlaskConical, section: 'Clinical Work' },
  // medopd-docs 131: clinic admins reach the same page as a tab of AI Settings; MCP Agents left the clinic menu.
  { path: '/doctor/clinic-ai-info',  label: 'Clinic Info for AI', icon: BookOpen, section: 'Clinical Work' },
  { path: '/doctor/revenue',         label: 'Revenue',      icon: TrendingUp,    section: 'Analytics & Reports' },
  { path: '/doctor/feedback',        label: 'Feedback',     icon: Star,          section: 'Analytics & Reports' },
  { path: '/reports',                label: 'Daily Report', icon: BarChart3,     section: 'Analytics & Reports' },
  // Lab (medopd-docs 153): the day's work, then set-up, then quality and reports. Receiving left the menu — the
  // worklist's "Receive tubes" button and its Receive tab are the same screen.
  { path: '/lab/front-desk',         label: 'Lab Front Desk', icon: ScanLine,     section: 'Lab' },
  { path: '/lab/tasks',              label: 'Lab Worklist', icon: Microscope,    section: 'Lab' },
  { path: '/lab/bills',              label: 'Lab Bills',    icon: ReceiptIcon,   section: 'Lab' },
  { path: '/lab/home-collections',   label: 'Lab Bookings', icon: Truck,         section: 'Lab' },
  { path: '/lab/tests',              label: 'Tests & Prices', icon: FlaskConical, section: 'Lab Set-up' },
  { path: '/lab/report-setup',       label: 'Report Set-up', icon: FileSignature, section: 'Lab Set-up' },
  { path: '/lab/referrers',          label: 'Referring Doctors', icon: UserRoundPlus, section: 'Lab Set-up' },
  { path: '/lab/price-lists',        label: 'Price Lists',  icon: Tags,          section: 'Lab Set-up' },
  { path: '/lab/result-rules',       label: 'Result Rules', icon: Calculator,    section: 'Lab Set-up' },
  { path: '/lab/qc',                 label: 'Quality Control', icon: ClipboardCheck, section: 'Lab Quality & Reports' },
  { path: '/lab/inventory',          label: 'Lab Inventory', icon: Boxes,        section: 'Lab Quality & Reports' },
  { path: '/lab/analytics',          label: 'Lab Analytics', icon: LineChart,    section: 'Lab Quality & Reports' },
  { path: '/pharmacy/inventory',     label: 'Medication Stock', icon: Boxes,     section: null },
  { path: '/pharmacy/counter',       label: 'Counter Sale', icon: Receipt,     section: null },
  { path: '/pharmacy/sales',         label: 'Sales',        icon: ReceiptText, section: null },
  { path: '/pharmacy/medicine-orders', label: 'Online Medicine Orders', icon: ShoppingBag, section: null },
  { path: '/pharmacy/purchases',     label: 'Purchases',    icon: PackagePlus, section: null },
  { path: '/pharmacy/suppliers',     label: 'Suppliers',    icon: Truck,       section: null },
  { path: '/pharmacy/reorder',       label: 'Reorder List', icon: ListChecks,  section: null },
  { path: '/pharmacy/controlled-register', label: 'Controlled Register', icon: BookLock, section: null },
  { path: '/pharmacy/reports',       label: 'Pharmacy Reports', icon: BarChart3, section: null },
  { path: '/insurance',              label: 'Insurance Desk', icon: ShieldCheck, section: null },
  { path: '/admin/hospital-groups',  label: 'Hospital Groups', icon: Building2,   section: 'Management' },
  { path: '/admin/inventory',        label: 'Supplies & Consumables', icon: Boxes, section: 'Management' },
  { path: '/admin/settings',         label: 'Settings',     icon: Settings,      section: 'Management' },
  { path: '/admin/plan',             label: 'Your Plan',    icon: Wallet,        section: 'Management' },
  { path: '/admin/staff',            label: 'Staff',        icon: UsersRound,    section: 'Management' },
  { path: '/admin/fee-schedule',     label: 'Fee Schedule', icon: DollarSign,    section: 'Management' },
  { path: '/admin/payment-gateway',  label: 'Online Payments', icon: CreditCard, section: 'Management' },
  { path: '/admin/labs',             label: 'Partner Labs', icon: FlaskConical,  section: 'Management' },
  { path: '/admin/in-house-pharmacies', label: 'In-house Pharmacy', icon: Pill,   section: 'Management' },
  { path: '/admin/lab-catalog',      label: 'Lab Catalog',  icon: Microscope,    section: 'Management' },
  { path: '/settings/doctors',       label: 'Doctors',      icon: Stethoscope,   section: 'Management' },
  { path: '/settings/departments',   label: 'Departments',  icon: Building2,     section: 'Management' },
  { path: '/settings/forms',         label: 'Consultation Forms', icon: ClipboardList, section: 'Management' },
  { path: '/settings/specialty-packs', label: 'Specialty Packs', icon: Package,    section: 'Management' },
  { path: '/settings/specialty-features', label: 'Specialty Features', icon: ListChecks, section: 'Management' },
  { path: '/settings/consent-forms',  label: 'Consent Forms', icon: FileSignature, section: 'Management' },
  { path: '/settings/recall-rules',   label: 'Recall Rules', icon: CalendarClock, section: 'Patient Reminders' },
  { path: '/admin/erasure-requests',    label: 'Data Erasure',    icon: ShieldOff,   section: 'Management' },
  { path: '/admin/ai-settings',         label: 'AI Settings',     icon: Bot,         section: 'Management' },
  { path: '/admin/clinic-profile',      label: 'Clinic Profile',  icon: Building2,   section: 'Management' },
  { path: '/admin/public-profile',      label: 'Public Listing',  icon: Globe,       section: 'Management' },
  { path: '/admin/home-collection',     label: 'Lab Booking Settings', icon: Truck, section: 'Management' },
  { path: '/admin/medicine-ordering',   label: 'Medicine Ordering Settings', icon: ShoppingBag, section: 'Management' },
  { path: '/admin/insurance',           label: 'Insurance Settings', icon: ShieldCheck, section: 'Management' },
  { path: '/admin/engagement',          label: 'Engagement',      icon: Bell,         section: 'Management' },
  { path: '/admin/checkin-settings',    label: 'Check-In',        icon: ClipboardCheck, section: 'Management' },
  { path: '/admin/health-packages',     label: 'Packages',        icon: Package,     section: 'Management' },
  { path: '/admin/corporate',           label: 'Corporate',       icon: Building2,   section: 'Management' },
  { path: '/admin/revenue-intelligence',label: 'Revenue & Reports', icon: BarChart3, section: 'Analytics & Reports' },
  { path: '/admin/feedback-nps',        label: 'Patient Feedback', icon: Star,      section: 'Analytics & Reports' },
  { path: '/admin/quality-measures',    label: 'Quality Measures', icon: Activity,  section: 'Analytics & Reports' },
  { path: '/admin/follow-ups',          label: 'Follow-up Rules', icon: Bell,        section: 'Patient Reminders' },
  { path: '/admin/referrals',           label: 'Referral Tracking', icon: Share2,    section: 'Patient Reminders' },
  { path: '/growth',                    label: 'Patients to Recall', icon: TrendingUp, section: 'Patient Reminders' },
  { path: '/growth/campaigns',       label: 'Bulk Reminders', icon: Send,        section: 'Patient Reminders' },
  { path: '/profile',                label: 'My Profile',   icon: UserCircle,    section: null },
];


// Super admin nav — a platform-owner console, not a filtered clinic nav. super_admin has no
// clinicId (tenantScope: SYSTEM), so this deliberately excludes single-clinic operational items
// (appointments/billing/lab/etc.) — those pages either 404 for this role (no RequireRole match)
// or render blank (no active clinic to scope to). Every path below has been confirmed to allow
// super_admin in its route guard. Organized by business concern, not a flat dump.
const SUPER_ADMIN_NAV_SECTIONS: NavSection[] = [
  { items: [
    { label: 'Dashboard', path: '/admin',         icon: Home },
    { label: 'Mail',      path: '/notifications', icon: Mail },
  ]},
  { title: 'Hospital Network', items: [
    { label: 'Hospital Groups', path: '/admin/hospital-groups', icon: Building2 },
  ]},
  { title: 'Platform Catalog', items: [
    { label: 'Subscription Plans', path: '/admin/subscription-plans', icon: CreditCard },
    { label: 'Add-on Requests',    path: '/admin/addon-requests',     icon: Bot },
    { label: 'Medications',        path: '/admin/medications',        icon: Store },
    { label: 'Lab Metrics',        path: '/admin/lab-metrics',        icon: Microscope },
    { label: 'Pharmacies',         path: '/admin/pharmacy-approvals', icon: ShieldCheck },
  ]},
  { title: 'Compliance & Trust', items: [
    { label: 'Consent Policy', path: '/admin/consent-settings', icon: ShieldCheck },
    { label: 'Doctor Verification', path: '/admin/doctor-verifications', icon: ShieldCheck },
    { label: 'Lab Verification', path: '/admin/lab-verifications', icon: FlaskConical },
    { label: 'Clinic Software Requests', path: '/admin/clinic-account-requests', icon: Building2 },
    { label: 'Review Moderation', path: '/admin/review-moderation', icon: Star },
    { label: 'About Text Review', path: '/admin/about-text-review', icon: FileText },
    { label: 'Visitor Reports', path: '/admin/moderation-reports', icon: Flag },
    { label: 'Published Content Audit', path: '/admin/moderation-audit', icon: ShieldCheck },
    { label: 'Moderation Policy', path: '/admin/moderation-policy', icon: Scale },
    { label: 'Article Review', path: '/admin/article-review', icon: Newspaper },
    { label: 'Question Moderation', path: '/admin/question-moderation', icon: HelpCircle },
    { label: 'Photo Review', path: '/admin/photo-moderation', icon: Images },
    { label: 'Data Erasure',   path: '/admin/erasure-requests', icon: ShieldOff },
    { label: 'Audit Logs',     path: '/admin/audit',            icon: ShieldCheck },
  ]},
  { title: 'Platform Operations', items: [
    { label: 'Site Visitors',    path: '/admin/site-visitors',      icon: Globe },
    { label: 'Clinic Access',    path: '/admin/clinic-access',      icon: ShieldCheck },
    { label: 'App Versions',     path: '/admin/app-versions',       icon: Smartphone },
    { label: 'Nav Settings',     path: '/admin/nav-settings',       icon: Shield },
    { label: 'AI Safety Review', path: '/admin/ai-safety-review',   icon: Shield },
    { label: 'Error Logs',       path: '/admin/error-logs',         icon: AlertTriangle },
  ]},
  { title: 'Developer Tools', items: [
    { label: 'Test Data Studio', path: '/admin/test-data', icon: FlaskConical },
  ]},
  { items: [{ label: 'My Profile', path: '/profile', icon: UserCircle }] },
];

export function Layout({ children }: LayoutProps) {
  // Bound as `tx`: this file already uses `t` for something else.
  const { t: tx } = useTranslation('ui');
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { user, userData, clinic, signOut } = useAuth();
  const { visiblePaths, canAccessPath, moduleEnabled, role: permRole } = usePermissions();
  const isSuperAdmin = userData?.role === 'super_admin';
  // Multi-pharmacy accounts: restores the remembered pharmacy (X-Active-Pharmacy-Id) on any page, not only after the
  // dashboard has loaded, so a counter sale in a fresh tab goes to the chosen pharmacy.
  useActivePharmacy(userData?.role === 'pharmacy_admin' || userData?.role === 'pharmacy_staff');
  // "AI not in your plan" (medopd-docs 126): AI routes stay in the nav and open an upsell panel instead of the page,
  // but only for roles the route admits — anyone else still falls through to the route's own guard.
  const { aiModuleOff, requireAi } = useAiModule();
  // Result Rules holds the formulas and the change checks: hidden only when the clinic has both switched off (medopd-docs 152).
  const labFeatureOn = useLabFeatures();
  const resultRulesOff = !labFeatureOn(LAB_FEATURES.calculations) && !labFeatureOn(LAB_FEATURES.deltaChecks);
  const upsellReq = aiModuleOff && routeUpsellModule(location.pathname) ? getRouteRequirement(location.pathname) : null;
  const aiRouteBlocked = !!upsellReq && (!upsellReq.roles || (!!permRole && upsellReq.roles.includes(permRole)));
  const qc = useQueryClient();
  const { subscribe } = useWebSocketContext();
  const { activeClinicId, activeClinic, isMultiClinic } = useTenant();
  const { mode, toggleColorMode } = useContext(ColorModeContext);
  const isDark = mode === 'dark';
  // Clinical-shell tokens. Declared here, not beside sidebarGradient below, so it is
  // initialised before SidebarNavList's closure can ever read it.
  const d = design(mode);

  // Show active branch name for multi-clinic users; fall back to primary clinic name.
  //
  // super_admin is checked FIRST and separately. It is a platform-level role with clinicId null
  // and tenantScope SYSTEM — it belongs to no clinic by design — so it reached the final fallback
  // and rendered "Clinic Manager", which reads as though the platform owner is scoped to some
  // clinic. Anything clinic-shaped is wrong for this role, not just this particular wording.
  const displayClinicName = isSuperAdmin
    ? 'Clinvea Platform'
    : (isMultiClinic && activeClinic?.clinicName)
      ? activeClinic.clinicName
      : (clinic?.name || (userData?.role === 'group_admin' ? 'Hospital Group' : 'Clinic Manager'));

  // ── UI state ────────────────────────────────────────────────────────────
  const [mobileOpen,         setMobileOpen]         = useState(false);
  const [showHelpModal,      setShowHelpModal]       = useState(false);
  const [showShortcutsModal, setShowShortcutsModal]  = useState(false);
  const [showAiChat,         setShowAiChat]          = useState(false);
  const [profileAnchor,      setProfileAnchor]       = useState<HTMLElement | null>(null);
  const [collapsed,          setCollapsed]           = useState(() => localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === '1');
  // Sidebar navigation runs as a transition so the current page stays up while the next page's chunk and data
  // load, instead of flashing a spinner. A transition shows nothing by itself, though: the user used to see no
  // reaction to a click for the whole load (up to two seconds on a cold dev server). `navPending` drives a thin
  // progress bar and `pendingPath` highlights the clicked item at once (medopd-docs 105).
  const [navPending, startNavigation] = useTransition();
  const [pendingPath, setPendingPath] = useState<string | null>(null);
  useEffect(() => { if (!navPending) setPendingPath(null); }, [navPending]);

  // If a super_admin disables AI_ASSISTANT for this clinic while the chat panel is already open
  // (live via the PERMISSIONS_CHANGED websocket invalidation below), force it closed — hiding just
  // the toggle button isn't enough if the panel itself is already up.
  useEffect(() => {
    if (showAiChat && !isSuperAdmin && !moduleEnabled('AI_ASSISTANT')) setShowAiChat(false);
  }, [showAiChat, isSuperAdmin, moduleEnabled]);

  const toggleCollapsed = () => {
    setCollapsed((prev) => {
      const next = !prev;
      localStorage.setItem(SIDEBAR_COLLAPSED_KEY, next ? '1' : '0');
      return next;
    });
  };

  // Below lg the sidebar is always the narrow icon rail, whatever `collapsed` says — an expanded
  // 268px sidebar would swallow a phone screen. `railMode` is what the sidebar's own contents key
  // off; `collapsed` remains the user's desktop preference and is left untouched by viewport size.
  const isNarrowViewport = useMediaQuery((t: any) => t.breakpoints.down('lg'));
  // Phones and small tablets get no rail at all: every pixel of width goes to the page, and the
  // top bar's menu button opens the full, labelled navigation drawer instead.
  const isPhone = useMediaQuery((t: Theme) => t.breakpoints.down('md'));

  // Each screen eases in when you arrive at it. Played with the Web Animations API on the same element,
  // so nothing remounts and no page loses its state; skipped for people who ask for reduced motion.
  const pageRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = pageRef.current;
    if (!el || typeof el.animate !== 'function') return;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
    el.animate([{ opacity: 0, transform: 'translateY(6px)' }, { opacity: 1, transform: 'none' }],
      { duration: 260, easing: 'cubic-bezier(.2,.7,.2,1)' });
  }, [location.pathname]);
  const railMode = isNarrowViewport || collapsed;

  /**
   * The rail's menu button. On a narrow viewport there is nowhere to expand into, so it opens the
   * temporary Drawer overlay — which is what actually shows the labels there. On desktop it toggles
   * the persistent sidebar's width, as the old top-bar control did.
   */
  const handleMenuButton = () => {
    if (isNarrowViewport) setMobileOpen(true);
    else toggleCollapsed();
  };

  // Auto-collapse the sidebar on the Appointments screen — that page needs the width for the
  // appointment list + patient workspace. Doesn't touch the persisted preference (localStorage),
  // so it restores whatever the user had set once they navigate away.
  const collapsedBeforeAppointments = useRef<boolean | null>(null);
  useEffect(() => {
    if (location.pathname === '/tokens') {
      if (collapsedBeforeAppointments.current === null) {
        collapsedBeforeAppointments.current = collapsed;
        if (!collapsed) setCollapsed(true);
      }
    } else if (collapsedBeforeAppointments.current !== null) {
      setCollapsed(collapsedBeforeAppointments.current);
      collapsedBeforeAppointments.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);


  // ── Keyboard shortcuts ──────────────────────────────────────────────────
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      // Never while typing: Ctrl+B is Bold in the rich-text editor (and Ctrl+N / Ctrl+Shift+P mean nothing in a field). Plain Ctrl+P stays the browser's print..
      // A key the editor already handled arrives with defaultPrevented.
      const el = e.target as HTMLElement | null;
      if (e.defaultPrevented || (el && (el.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName)))) return;
      if ((e.ctrlKey || e.metaKey) && e.key === 'n' && !e.shiftKey) { e.preventDefault(); startTransition(() => { void navigate('/tokens'); }); }
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key.toLowerCase() === 'p') { e.preventDefault(); startTransition(() => { void navigate('/patients'); }); }
      if ((e.ctrlKey || e.metaKey) && e.key === 'b' && !e.shiftKey) { e.preventDefault(); startTransition(() => { void navigate('/billing'); }); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [navigate]);

  // ── WebSocket: instant permission invalidation ──────────────────────────
  // When the super admin changes nav visibility, module access, or role permissions,
  // the backend pushes a PERMISSIONS_CHANGED message to /topic/permissions/{clinicId}
  // (and /topic/permissions/global for route-registry edits). We invalidate the
  // my-permissions cache immediately so the user's sidebar updates without waiting
  // for the 30-second staleTime to expire.
  useEffect(() => {
    if (!user?.id || isSuperAdmin) return; // super_admin nav is static; no need to react

    const invalidate = () =>
      qc.invalidateQueries({ queryKey: ['my-permissions', user.id, activeClinicId] });

    const unsubs: Array<() => void> = [];

    // Subscribe to active branch's permissions topic (falls back to primary clinic for non-group users)
    const branchId = activeClinicId ?? userData?.clinicId;
    if (branchId) {
      unsubs.push(subscribe(`/topic/permissions/${branchId}`, invalidate));
    }
    // Route-registry edits affect all clinics
    unsubs.push(subscribe('/topic/permissions/global', invalidate));

    return () => unsubs.forEach(fn => fn());
  }, [user?.id, activeClinicId, userData?.clinicId, isSuperAdmin, subscribe, qc]);

  // ── Nav sections ───────────────────────────────────────────────────────
  const navSections = useMemo<NavSection[]>(() => {
    if (isSuperAdmin) return SUPER_ADMIN_NAV_SECTIONS;

    // Setup not complete: show only setup link
    // group_admin and regional_admin have no clinic so setupCompleted is always undefined —
    // they must be exempted or they'd be permanently stuck on the setup screen.
    if (!clinic?.setupCompleted &&
        userData?.role !== 'lab_assistant' &&
        userData?.role !== 'lab_admin' &&
        userData?.role !== 'lab_staff' &&
        userData?.role !== 'pharmacy_admin' &&
        userData?.role !== 'pharmacy_staff' &&
        userData?.role !== 'group_admin' &&
        userData?.role !== 'regional_admin') {
      return [
        { items: [{ label: 'Complete Setup', path: '/onboarding', icon: Settings }] },
        { items: [{ label: 'My Profile',     path: '/profile', icon: UserCircle }] },
      ];
    }

    // Build from server-controlled visiblePaths
    const visibleSet = new Set<string>(visiblePaths ?? []);
    const sectionItems = new Map<string, NavSection['items']>();

    for (const item of MASTER_NAV) {
      // Patient Reminders (growth/follow-up campaigns) isn't functional for doctors — hide it
      // for that role rather than show a dead-end section.
      if (item.section === 'Patient Reminders' && userData?.role === 'doctor') continue;
      // AI items (Lab AI Review, Clinic Info for AI, AI Settings) are hidden while the clinic has no AI module.
      // Opening one of those URLs directly still shows the upsell panel (medopd-docs 126).
      if (aiModuleOff && routeUpsellModule(item.path)) continue;
      if (resultRulesOff && item.path === '/lab/result-rules') continue;

      // Profile is always shown; other items must be in visiblePaths once loaded AND pass the granular
      // permission check — the server's visiblePaths can be broader than the user's actual permissions,
      // which is why items were showing for roles that couldn't use them.
      const show = item.path === '/profile' ||
        (visiblePaths !== null && visibleSet.has(item.path) && canAccessPath(item.path));
      if (!show) continue;

      const key = item.section ?? '__top__';
      if (!sectionItems.has(key)) sectionItems.set(key, []);
      sectionItems.get(key)!.push({ label: item.label, path: item.path, icon: item.icon });
    }

    const result: NavSection[] = [];
    const topItems = sectionItems.get('__top__');
    if (topItems?.length) result.push({ items: topItems });
    for (const [key, items] of sectionItems) {
      if (key === '__top__' || !items.length) continue;
      result.push({ title: key, items });
    }
    return result;
  }, [isSuperAdmin, visiblePaths, clinic?.setupCompleted, userData?.role, canAccessPath, aiModuleOff, resultRulesOff]);

  const allNavItems = useMemo(() => navSections.flatMap(s => s.items), [navSections]);

  // Path → label for the top bar's crumb. Built from the SAME nav items the sidebar renders, so a
  // route renamed in MASTER_NAV cannot end up with a crumb that disagrees with its nav entry.
  const navLabelsByPath = useMemo(
    () => new Map(allNavItems.map(i => [i.path, navLabel(t, i.label)] as const)),
    [allNavItems, t],
  );

  // All nav items live in the sidebar — including the untitled top-level ones (Dashboard, Mail,
  // Orders, Inventory) and the high-frequency ones (Appointments, Register, Patients) that used to
  // be promoted into the top bar. The top bar is utility-only now (search, notifications, profile).
  const sidebarSections = useMemo(
    () => navSections.filter(s => s.items.length > 0),
    [navSections],
  );

  const mobileSections = sidebarSections;

  // ── Helpers ─────────────────────────────────────────────────────────────
  const isActive = (path: string) => location.pathname === path;

  const goTo = (path: string) => {
    if (path !== location.pathname) {
      setPendingPath(path);
      preloadRoute(path, userData?.role);
    }
    startNavigation(() => { void navigate(path); });
    setMobileOpen(false);
  };
  // Warm the page's chunk while the pointer is still on the way to the click (hover/focus arrive a few hundred
  // milliseconds earlier); once per page load, see lib/routePreload.
  const warm = (path: string) => preloadRoute(path, userData?.role);

  // ── Sidebar nav list — shared by the persistent desktop rail and the mobile drawer ──────
  // Super admin: how much is waiting in each review queue, shown on the menu so nothing sits unnoticed
  // (a doctor's answer used to wait in Question Moderation with nothing pointing at it).
  const { data: pendingCounts } = useQuery({
    queryKey: MODERATION_COUNTS_KEY,
    queryFn: async () => {
      const res = await api.healthQa.review.pendingCounts();
      if (res.error) throw new Error(res.error);
      return res.data ?? null;
    },
    enabled: isSuperAdmin,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
    staleTime: 30_000,
  });
  const waitingByPath: Record<string, number> = pendingCounts ? {
    '/admin/question-moderation': pendingCounts.questions + pendingCounts.answers,
    '/admin/article-review': pendingCounts.articles,
    '/admin/review-moderation': pendingCounts.reviews,
    '/admin/about-text-review': pendingCounts.aboutText ?? 0,
    '/admin/photo-moderation': pendingCounts.photos,
    '/admin/doctor-verifications': pendingCounts.doctorVerifications,
    '/admin/lab-verifications': pendingCounts.labVerifications ?? 0,
    '/admin/clinic-account-requests': pendingCounts.clinicSoftware ?? 0,
    '/admin/addon-requests': pendingCounts.addonRequests ?? 0,
  } : {};

  const SidebarNavList = ({ sections, mini = false }: { sections: NavSection[]; mini?: boolean }) => (
    <List sx={{ px: 1.25, py: 0.75 }}>
      {sections.map((section, si) => (
        <Box key={si}>
          {section.title && !mini && (
            <ListSubheader sx={{
              bgcolor: 'transparent', color: d.navLabel,
              fontSize: '0.655rem', fontWeight: 600, textTransform: 'uppercase',
              letterSpacing: '0.11em', px: 1.25, lineHeight: '2.6',
            }}>
              {navSectionLabel(t, section.title)}
            </ListSubheader>
          )}
          {mini && si > 0 && <Divider sx={{ my: 1, borderColor: 'rgba(255,255,255,0.1)' }} />}
          {section.items.map(item => {
            const Icon = item.icon;
            const active = isActive(item.path) || item.path === pendingPath;
            const waiting = waitingByPath[item.path] ?? 0;
            const button = (
              <ListItemButton
                key={item.path}
                onClick={() => goTo(item.path)}
                onMouseEnter={() => warm(item.path)}
                onFocus={() => warm(item.path)}
                onTouchStart={() => warm(item.path)}
                selected={active}
                sx={{
                  borderRadius: '8px', mb: '2px', py: 1.05,
                  justifyContent: mini ? 'center' : 'flex-start',
                  color: active ? '#fff' : d.navText,
                  // Solid brand fill for the active route rather than a translucent white wash:
                  // at rail width the wash was nearly invisible against the navy.
                  '&.Mui-selected':       { bgcolor: d.brand, color: '#fff' },
                  '&.Mui-selected:hover': { bgcolor: d.brand, color: '#fff' },
                  '&:hover':              { bgcolor: d.navHover, color: '#fff' },
                }}
              >
                <ListItemIcon sx={{ minWidth: mini ? 0 : 32, justifyContent: 'center', color: 'inherit' }}>
                  <MuiBadge color="error" variant="dot" invisible={!mini || waiting === 0}>
                    <Icon size={17} />
                  </MuiBadge>
                </ListItemIcon>
                {!mini && (
                  <ListItemText primary={navLabel(t, item.label)} primaryTypographyProps={{ fontWeight: active ? 700 : 500, fontSize: '0.86rem' }} />
                )}
                {!mini && waiting > 0 && (
                  <Box component="span" aria-label={tx('waitingForReview', { count: waiting })}
                    sx={{ ml: 1, minWidth: 22, px: 0.75, py: 0.1, borderRadius: 10, bgcolor: '#e5484d', color: '#fff',
                      fontSize: '0.7rem', fontWeight: 700, textAlign: 'center', lineHeight: 1.6, fontVariantNumeric: 'tabular-nums' }}>
                    {waiting > 99 ? '99+' : waiting}
                  </Box>
                )}
              </ListItemButton>
            );
            return mini ? (
              <Tooltip key={item.path} title={waiting > 0 ? `${navLabel(t, item.label)} · ${tx('waitingForReview', { count: waiting })}` : navLabel(t, item.label)} placement="right">
                {button}
              </Tooltip>
            ) : button;
          })}
        </Box>
      ))}
    </List>
  );

  // ── Mobile drawer (temporary, always full-width labels) ─────────────────
  const mobileDrawerContent = (
    <Box sx={{
      width: 'min(86vw, 300px)', height: '100%',
      background: isDark ? 'linear-gradient(180deg, #060d1f 0%, #0b1728 100%)' : 'linear-gradient(180deg, #0f1e47 0%, #1b3582 100%)',
      color: 'white', display: 'flex', flexDirection: 'column',
    }}>
      {/* Drawer header */}
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ px: 2.5, py: 2.25 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box
            component="img"
            src="/assets/images/clinvea-logo-dark.png?v=20261007"
            alt="ClinVea"
            sx={{ height: 28, width: 'auto', display: 'block' }}
          />
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: '0.62rem', mt: 0.25, display: 'block' }}>
            {displayClinicName}
          </Typography>
        </Box>
        <IconButton size="small" onClick={() => setMobileOpen(false)} sx={{ color: 'rgba(255,255,255,0.7)' }}>
          <X size={18} />
        </IconButton>
      </Stack>

      <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />

      {/* Active branch chip */}
      {isMultiClinic && activeClinic && (
        <Box sx={{ px: 2, pt: 1, pb: 0.5 }}>
          <Chip
            size="small"
            icon={<Building2 size={11} />}
            label={activeClinic.clinicName}
            sx={{
              bgcolor: 'rgba(99,102,241,0.2)',
              color: 'rgba(199,210,254,0.9)',
              fontSize: '0.68rem',
              fontWeight: 600,
              border: '1px solid rgba(99,102,241,0.25)',
              '& .MuiChip-icon': { color: 'rgba(165,180,252,0.8)' },
            }}
          />
        </Box>
      )}

      <Box sx={{ flex: 1, overflowY: 'auto', ...hiddenScrollbarSx }}>
        <SidebarNavList sections={mobileSections} />
      </Box>

      <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />

      {/* User footer */}
      <Stack direction="row" alignItems="center" spacing={1.25} sx={{ px: 2.5, py: 1.5 }}>
        <Avatar
          src={userData?.avatarSmall || userData?.avatarThumbnail ? avatarSrc(userData?.id, 'small') : undefined}
          sx={{ width: 32, height: 32, bgcolor: 'rgba(99,102,241,0.8)', color: 'white', fontSize: '0.78rem', fontWeight: 700 }}
          imgProps={{ loading: 'lazy' }}
        >
          {userData?.name?.[0]?.toUpperCase() || 'U'}
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" fontWeight={700} noWrap>{userData?.name || tx('defaultUserName')}</Typography>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', textTransform: 'capitalize' }}>
            {enumLabel('role', userData?.role || 'staff')}
          </Typography>
        </Box>
        <Tooltip title={tx('signOut')}>
          <IconButton onClick={signOut} size="small" sx={{ color: 'rgba(255,255,255,0.5)', '&:hover': { color: '#fca5a5', bgcolor: 'rgba(239,68,68,0.15)' } }}>
            <LogOut size={15} />
          </IconButton>
        </Tooltip>
      </Stack>
    </Box>
  );
  // Flat, not a gradient: this design treats the sidebar as a recessive landmark, not a brand banner.
  const sidebarGradient = d.nav;

  // ── Render ──────────────────────────────────────────────────────────────
  // No overflow rule on the outer Box below — an ancestor with overflow-x set (even just on one
  // axis) makes the browser compute overflow-y as auto too, turning it into the sticky sidebar's
  // scroll container instead of the viewport, which silently breaks `position: sticky` on it. The
  // right-hand content column has its own overflowX: hidden and isn't an ancestor of the sidebar,
  // so it's safe to clip there instead.
  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>

      {/* ─── PERSISTENT SIDEBAR — all breakpoints ────────────────────────── */}
      <Box
        component="nav"
        sx={{
          // Visible at EVERY breakpoint. The top bar (and with it the hamburger) is gone, so the
          // rail is the only way to reach navigation — hiding it below lg would leave phones and
          // tablets with no way to navigate at all.
          //
          // Below lg the rail is always the narrow icon form regardless of `collapsed`: an expanded
          // 268px sidebar would cover most of a phone screen. Tapping the menu icon there opens the
          // temporary Drawer overlay instead, which is what shows the labels.
          width: {
            md: SIDEBAR_WIDTH_RAIL,
            lg: collapsed ? SIDEBAR_WIDTH_COLLAPSED : SIDEBAR_WIDTH,
          },
          flexShrink: 0, display: { xs: 'none', md: 'flex' }, flexDirection: 'column',
          position: 'sticky', top: 0, height: '100vh', zIndex: 1200,
          background: sidebarGradient, color: 'white',
          transition: 'width 0.2s ease',
        }}
      >
        {/*
          Brand row — and the only home of the collapse/expand toggle.

          Collapsed, the rail has room for one thing at the top, and of the two candidates the logo
          is the expendable one: it is decoration, while the toggle is the sole way back to the
          labelled sidebar (below lg, the sole way to reach navigation at all). So the ClinVea icon
          is dropped in rail mode and the menu button occupies that spot. Expanded, the full logo
          and clinic name show and the toggle sits at the end of the same row. The toggle used to
          live in a strip above the profile block; it is not duplicated there any more, so there is
          still exactly one of it.
        */}
        <Stack
          direction="row" alignItems="center" spacing={1.25}
          sx={{ px: railMode ? 1 : 2, py: 2.25, justifyContent: railMode ? 'center' : 'flex-start' }}
        >
          {!railMode && (
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Box
                component="img"
                src="/assets/images/clinvea-logo-dark.png?v=20261007"
                alt="ClinVea"
                sx={{ height: 54, width: 'auto', display: 'block' }}
              />
              <Typography sx={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.6rem', lineHeight: 1.6, textTransform: 'uppercase', letterSpacing: '0.06em', mt: 0.25 }} noWrap>
                {displayClinicName}
              </Typography>
            </Box>
          )}
          <Tooltip title={isNarrowViewport ? tx('openNavigation') : (collapsed ? tx('expandSidebar') : tx('collapseSidebar'))} placement="right">
            <IconButton
              onClick={handleMenuButton}
              size="small"
              aria-label={isNarrowViewport ? tx('openNavigation') : (collapsed ? tx('expandSidebar') : tx('collapseSidebar'))}
              sx={{ color: 'inherit', flexShrink: 0 }}
            >
              <MenuIcon size={railMode ? 22 : 18} />
            </IconButton>
          </Tooltip>
        </Stack>

        <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />

        {/* Active branch chip */}
        {isMultiClinic && activeClinic && !railMode && (
          <Box sx={{ px: 2, pt: 1, pb: 0.5 }}>
            <Chip
              size="small"
              icon={<Building2 size={11} />}
              label={activeClinic.clinicName}
              sx={{
                bgcolor: 'rgba(99,102,241,0.2)', color: 'rgba(199,210,254,0.9)',
                fontSize: '0.68rem', fontWeight: 600, border: '1px solid rgba(99,102,241,0.25)',
                '& .MuiChip-icon': { color: 'rgba(165,180,252,0.8)' },
              }}
            />
          </Box>
        )}

        <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', ...hiddenScrollbarSx }}>
          <SidebarNavList sections={sidebarSections} mini={railMode} />
        </Box>

        {/* Switch Profile — reuses the same account menu the top bar's avatar used (My Profile,
            theme, shortcuts, help, sign out) so there's one menu to maintain, not two. */}
        <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />
        <Box
          onClick={(e) => setProfileAnchor(e.currentTarget)}
          sx={{
            display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer',
            px: railMode ? 1 : 1.75, py: 1.5, justifyContent: railMode ? 'center' : 'flex-start',
            '&:hover': { bgcolor: 'rgba(255,255,255,0.06)' },
          }}
        >
          <Avatar
            src={userData?.avatarSmall || userData?.avatarThumbnail ? avatarSrc(userData?.id, 'small') : undefined}
            sx={{ width: 26, height: 26, bgcolor: 'rgba(99,102,241,0.8)', color: 'white', fontSize: '0.68rem', fontWeight: 700 }}
            imgProps={{ loading: 'lazy' }}
          >
            {userData?.name?.[0]?.toUpperCase() || 'U'}
          </Avatar>
          {!collapsed && (
            <>
              <Typography sx={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.78rem', fontWeight: 600, flex: 1 }} noWrap>
                {tx('switchProfile')}
              </Typography>
              <ChevronsUpDown size={14} style={{ color: 'rgba(255,255,255,0.5)', flexShrink: 0 }} />
            </>
          )}
        </Box>
      </Box>

      {/* ─── MOBILE DRAWER ───────────────────────────────────────────────── */}
      <Drawer variant="temporary" open={mobileOpen} onClose={() => setMobileOpen(false)} ModalProps={{ keepMounted: true }}>
        {mobileDrawerContent}
      </Drawer>

      {/* ─── RIGHT COLUMN: content only. The top bar was removed application-wide; its
          controls now live in the sidebar utility strip. ───────────────────── */}
      <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', overflowX: 'hidden' }}>
        {/* Profile menu — triggered from the top bar */}
        <Menu
          anchorEl={profileAnchor}
          open={!!profileAnchor}
          onClose={() => setProfileAnchor(null)}
          PaperProps={{ elevation: 4, sx: { mt: 1, minWidth: 230, borderRadius: 2.5, overflow: 'hidden' } }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        >
          {/* User info header */}
          <Box sx={{ px: 2, py: 1.5, bgcolor: 'action.hover' }}>
            <Typography fontWeight={700} fontSize="0.88rem" noWrap>{userData?.name}</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'capitalize' }}>
              {enumLabel('role', userData?.role)}
            </Typography>
          </Box>
          <Divider />

          {/* My Profile */}
          <MenuItem
            onClick={() => { startTransition(() => { void navigate('/profile'); }); setProfileAnchor(null); }}
            sx={{ gap: 1.5, py: 1.1, fontSize: '0.85rem' }}
          >
            <UserCircle size={16} />
            {tx('myProfile')}
          </MenuItem>
          <Divider />

          {/* Theme toggle */}
          <MenuItem
            onClick={() => { toggleColorMode(); setProfileAnchor(null); }}
            sx={{ gap: 1.5, py: 1.1, fontSize: '0.85rem' }}
          >
            {isDark ? <Sun size={16} /> : <Moon size={16} />}
            {isDark ? tx('switchToLightMode') : tx('switchToDarkMode')}
          </MenuItem>

          {/* Keyboard Shortcuts */}
          <MenuItem
            onClick={() => { setShowShortcutsModal(true); setProfileAnchor(null); }}
            sx={{ gap: 1.5, py: 1.1, fontSize: '0.85rem' }}
            data-tour="help-menu"
          >
            <Keyboard size={16} />
            {tx('keyboardShortcuts')}
          </MenuItem>

          {/* Help & Tutorials */}
          <MenuItem
            onClick={() => { setShowHelpModal(true); setProfileAnchor(null); }}
            sx={{ gap: 1.5, py: 1.1, fontSize: '0.85rem' }}
          >
            <HelpCircle size={16} />
            {tx('helpAndTutorials')}
          </MenuItem>
          <Divider />

          {/* Sign out */}
          <MenuItem
            onClick={signOut}
            sx={{ gap: 1.5, py: 1.1, fontSize: '0.85rem', color: 'error.main', '&:hover': { bgcolor: 'rgba(239,68,68,0.08)' } }}
          >
            <LogOut size={16} />
            {tx('signOut2')}
          </MenuItem>
        </Menu>

        {/* Application top bar — reinstated by the approved design. Search, branch switching,
            notifications and AI chat are passed in as children rather than re-implemented, so
            they moved here from the sidebar strip instead of being duplicated. */}
        <AppTopBar
          mode={mode}
          onToggleColorMode={toggleColorMode}
          onHelp={() => setShowHelpModal(true)}
          navLabels={navLabelsByPath}
          shiftLabel={activeClinic?.clinicName ?? null}
          onOpenMenu={isPhone ? () => setMobileOpen(true) : undefined}
        >
          {/* The patient-search API refuses pharmacy roles, so the bar would only ever error there. */}
          {!(userData?.role === 'pharmacy_admin' || userData?.role === 'pharmacy_staff') && <QuickPatientSearch />}
          <BranchSwitcher compact />
          <NotificationBell />
          {/* Shown on every plan (medopd-docs 126): without the AI module the click opens the upsell dialog. */}
          <AiChatFab onClick={() => { if (requireAi(tx('aiAssistant'))) setShowAiChat(prev => !prev); }} />
        </AppTopBar>

        {/* Active branch context bar — shown for multi-clinic non-super-admin users */}
        {isMultiClinic && !isSuperAdmin && activeClinic && (
          <Box sx={{
            background: isDark
              ? 'linear-gradient(to right, rgba(30,58,138,0.25), rgba(29,78,216,0.15))'
              : 'linear-gradient(to right, rgba(15,30,71,0.07), rgba(30,63,173,0.04))',
            borderBottom: `1px solid ${isDark ? 'rgba(99,102,241,0.15)' : 'rgba(30,58,138,0.1)'}`,
            px: { xs: 2, md: 3 }, py: 0.55, display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0,
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, px: 0.9, py: 0.25, borderRadius: 1, bgcolor: isDark ? 'rgba(99,102,241,0.2)' : 'rgba(30,58,138,0.08)' }}>
              <Building2 size={11} color={isDark ? '#818cf8' : '#1e3a8a'} />
              <Typography sx={{ fontSize: '0.67rem', fontWeight: 700, color: isDark ? '#a5b4fc' : '#1e3a8a', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                {tx('branch')}
              </Typography>
            </Box>
            <Typography variant="caption" sx={{ color: isDark ? '#c7d2fe' : '#1e3a8a', fontWeight: 700, fontSize: '0.75rem' }}>
              {activeClinic.clinicName}
            </Typography>
            {(activeClinic.city || activeClinic.state) && (
              <Typography variant="caption" sx={{ color: isDark ? 'rgba(199,210,254,0.5)' : 'rgba(30,58,138,0.55)', fontSize: '0.73rem' }}>
                · {[activeClinic.city, activeClinic.state].filter(Boolean).join(', ')}
              </Typography>
            )}
          </Box>
        )}

        {/* Page content */}
        <Box
          component="main"
          sx={{
            flex: 1, minWidth: 0, width: '100%',
            px: { xs: 1.5, sm: 2, md: 3 },
            pt: { xs: 1.5, sm: 2, md: 2 },
            pb: { xs: 12, sm: 10, md: 5 },
            transition: 'background-color .3s ease',
            // Flat, per the design's --bg. The gradient tinted the middle of every long page a
            // different colour from its top, which fought the flat white cards sitting on it.
            background: d.bg,
          }}
        >
          {navPending && (
            <LinearProgress
              aria-label={tx('loadingPage', 'Loading page')}
              sx={{ position: 'fixed', top: 0, left: 0, right: 0, height: 3, zIndex: (th) => th.zIndex.modal + 1 }}
            />
          )}
          <Container maxWidth={false} disableGutters sx={{ width: '100%', minWidth: 0 }}>
            <Box ref={pageRef}>{aiRouteBlocked ? <AiUpsellPanel featureLabel={navLabelsByPath.get(location.pathname)} /> : children}</Box>
          </Container>
        </Box>
      </Box>

      {showHelpModal      && <HelpModal onClose={() => setShowHelpModal(false)} />}
      {/* Follows background stock imports on every page and says when each ends (medopd-docs 142). */}
      <PharmacyImportTracker />
      {showShortcutsModal && <KeyboardShortcutsModal open={showShortcutsModal} onClose={() => setShowShortcutsModal(false)} />}
      <AiStaffChatPanel open={showAiChat} onClose={() => setShowAiChat(false)} />
      {/* Signed in while "Delete my account" is scheduled: offer to keep the account. */}
      <PendingDeletionDialog audience="staff" onSignOut={() => { void signOut(); }} />

      <LearnMoreButton />
      <GlobalPatientWindows />
      <MinimizedPatientTabs />
    </Box>
  );
}
